"use client";

import posthog from "posthog-js";
import { useEffect, useMemo, useState } from "react";
import Popup from "reactjs-popup";
import { PRO_BENEFITS } from "@/src/lib/pricing";
import { useAuth0 } from "@auth0/auth0-react";
import { handlePayment } from "@/src/helpers/stripe";
import { formatCountdown, formatLocalReset } from "@/src/helpers/usageTime";
import { useRouter } from "next/navigation";

const QuarkleImage = "/images/quarkle.png";


export default function UsageLimitModal({ open, closeModal, tier, resetAtUtc }) {
  const router = useRouter();
  const {
    user,
    isAuthenticated,
    loginWithPopup,
    getAccessTokenSilently,
    logout,
  } = useAuth0();
  const [initiatePayment, setInitiatePayment] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isYearly, setIsYearly] = useState(true);
  const [tickKey, setTickKey] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(() => setTickKey((k) => k + 1), 30000);
    return () => clearInterval(id);
  }, [open]);

  function redirectToStripe() {
    const plan = isYearly ? "pro-yearly" : "pro";
    return handlePayment(
      plan,
      user,
      router,
      setError,
      setIsLoading,
      getAccessTokenSilently,
      logout,
    );
  }

  useEffect(() => {
    if (isAuthenticated && initiatePayment)
      redirectToStripe().then(() => setInitiatePayment(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, initiatePayment]);

  async function handleProPayment() {
    posthog.capture("usage_limit_upgrade_clicked");
    if (!isAuthenticated) {
      try {
        await loginWithPopup();
      } catch (e) {
        setError(e);
      }
    }
    setInitiatePayment(true);
  }

  const countdown = useMemo(
    () => formatCountdown(resetAtUtc),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resetAtUtc, tickKey],
  );
  const localReset = useMemo(
    () => formatLocalReset(resetAtUtc),
    [resetAtUtc],
  );

  const isBasic = tier !== "pro";

  return (
    <Popup
      open={open}
      closeOnDocumentClick
      onClose={closeModal}
      overlayStyle={{
        background: "var(--qk-scrim)",
        backdropFilter: "blur(3px)",
      }}
      contentStyle={{
        background: "transparent",
        border: "none",
        padding: 0,
        width: "auto",
      }}
    >
      <div
        className="qk-editor qk-paper-bg relative"
        style={{
          width: "min(92vw, 520px)",
          maxHeight: "92vh",
          overflowY: "auto",
          border: "1.5px solid var(--qk-ink)",
          boxShadow: "8px 8px 0 var(--qk-pink-deep)",
          borderRadius: 14,
          color: "var(--qk-ink)",
        }}
      >
        <div
          className="qk-caps absolute z-10"
          style={{
            top: 18,
            left: -8,
            padding: "5px 14px",
            background: "var(--qk-lime)",
            color: "var(--qk-ink)",
            border: "1.5px solid var(--qk-ink)",
            transform: "rotate(-3deg)",
            boxShadow: "2px 2px 0 var(--qk-ink)",
            fontSize: 10,
          }}
        >
          {isBasic ? "today's limit reached" : "see you at 03:30 ct"}
        </div>

        <button
          type="button"
          aria-label="close"
          className="absolute z-10 flex h-8 w-8 items-center justify-center rounded-full"
          style={{
            top: 14,
            right: 14,
            border: "1px solid var(--qk-rule)",
            background: "var(--qk-paper-3)",
            color: "var(--qk-ink)",
          }}
          onClick={closeModal}
        >
          <i className="pi pi-times text-[11px]"></i>
        </button>

        <div className="px-8 pt-14 pb-6">
          <div className="flex items-center justify-between">
            <span className="qk-caps" style={{ color: "var(--qk-pink-deep)" }}>
              {isBasic ? "quarkle pro · keep writing" : "daily limit hit"}
            </span>
            <img
              src={QuarkleImage}
              alt=""
              className="h-10 w-10 rounded-full"
              style={{ border: "1.5px solid var(--qk-ink)" }}
            />
          </div>

          <h2
            className="qk-tanker mt-4"
            style={{
              fontSize: "clamp(30px, 5vw, 42px)",
              lineHeight: 0.95,
              letterSpacing: "-0.03em",
              margin: 0,
            }}
          >
            {isBasic ? (
              <>
                you hit the wall,{" "}
                <span
                  className="qk-display"
                  style={{ fontStyle: "italic", color: "var(--qk-pink-deep)" }}
                >
                  let's tear it down.
                </span>
              </>
            ) : (
              <>
                quiet hours,{" "}
                <span
                  className="qk-display"
                  style={{ fontStyle: "italic", color: "var(--qk-pink-deep)" }}
                >
                  on us.
                </span>
              </>
            )}
          </h2>

          <p
            className="qk-display mt-3"
            style={{
              fontSize: 16,
              fontStyle: "italic",
              opacity: 0.84,
              lineHeight: 1.55,
              color: "var(--qk-ink)",
              margin: 0,
            }}
          >
            {isBasic
              ? "you've used today's free tokens. pro gives you 200× the room."
              : "you've used today's pro allotment. it resets at 03:30 america/chicago."}
          </p>

          <div className="qk-dash-rule mt-5" />

          {isBasic ? (
            <ul
              className="mt-4 space-y-2.5"
              style={{
                fontFamily: "'Satoshi', system-ui, sans-serif",
                fontSize: 14,
                margin: 0,
                padding: 0,
                listStyle: "none",
              }}
            >
              {PRO_BENEFITS.map((b) => (
                <li key={b.emp} className="flex items-baseline gap-2.5">
                  <span
                    style={{
                      color: "var(--qk-moss)",
                      fontSize: 12,
                      flexShrink: 0,
                      lineHeight: 1,
                    }}
                  >
                    ✦
                  </span>
                  <span>
                    <span style={{ fontWeight: 600, color: "var(--qk-ink)" }}>
                      {b.emp}
                    </span>{" "}
                    <span style={{ color: "var(--qk-ink)", opacity: 0.78 }}>
                      {b.rest}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          <div
            className="mt-5"
            style={{
              background: "var(--qk-paper-3)",
              border: "1px solid var(--qk-rule)",
              borderRadius: 12,
              padding: "14px 16px",
            }}
          >
            <div
              className="qk-caps"
              style={{ color: "var(--qk-muted)", marginBottom: 6 }}
            >
              resets in
            </div>
            {countdown ? (
              <div
                className="qk-tanker"
                style={{
                  fontSize: 28,
                  lineHeight: 1,
                  color: "var(--qk-ink)",
                }}
              >
                {String(countdown.hours).padStart(2, "0")}h{" "}
                {String(countdown.minutes).padStart(2, "0")}m
              </div>
            ) : (
              <div className="qk-tanker" style={{ fontSize: 22 }}>
                soon
              </div>
            )}
            {localReset && (
              <div
                className="qk-mono mt-1"
                style={{
                  fontSize: 11,
                  color: "var(--qk-muted)",
                  letterSpacing: "0.04em",
                }}
              >
                {localReset.toLowerCase()} · 03:30 america/chicago
              </div>
            )}
          </div>

          {error && (
            <p
              className="qk-hand mt-3"
              style={{ color: "var(--qk-clay)", fontSize: 15 }}
            >
              {error.message}
            </p>
          )}

          {isBasic ? (
            <>
              <div
                className="mt-5 inline-flex"
                style={{
                  gap: 2,
                  padding: 3,
                  background: "var(--qk-paper)",
                  border: "1px solid var(--qk-rule)",
                  borderRadius: 999,
                }}
              >
                {[
                  { k: false, label: "monthly" },
                  { k: true, label: "yearly" },
                ].map((o) => {
                  const on = isYearly === o.k;
                  return (
                    <button
                      key={o.label}
                      type="button"
                      onClick={() => setIsYearly(o.k)}
                      className="qk-mono"
                      style={{
                        padding: "5px 14px",
                        borderRadius: 999,
                        fontSize: 10,
                        textTransform: "uppercase",
                        letterSpacing: "0.14em",
                        color: on ? "var(--qk-on-pink)" : "var(--qk-ink)",
                        background: on ? "var(--qk-pink-deep)" : "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: on ? 600 : 500,
                        transition: "all 0.18s ease",
                      }}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-5 flex items-center gap-2">
                <button
                  type="button"
                  className="qk-pill"
                  onClick={closeModal}
                >
                  not now
                </button>
                <button
                  type="button"
                  onClick={handleProPayment}
                  disabled={isLoading}
                  className="qk-pill flex-1 justify-center"
                  style={{
                    background: "var(--qk-pink-deep)",
                    color: "var(--qk-on-pink)",
                    borderColor: "var(--qk-ink)",
                    padding: "10px 16px",
                    fontSize: 12,
                    boxShadow: "3px 3px 0 var(--qk-ink)",
                    cursor: isLoading ? "wait" : "pointer",
                  }}
                >
                  {isLoading ? (
                    <span className="pi pi-spinner pi-spin"></span>
                  ) : (
                    <>start pro →</>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="mt-5 flex items-center justify-end">
              <button
                type="button"
                onClick={closeModal}
                className="qk-pill"
                style={{
                  background: "var(--qk-pink-deep)",
                  color: "var(--qk-on-pink)",
                  borderColor: "var(--qk-ink)",
                  padding: "10px 16px",
                  fontSize: 12,
                  boxShadow: "3px 3px 0 var(--qk-ink)",
                }}
              >
                got it
              </button>
            </div>
          )}
        </div>
      </div>
    </Popup>
  );
}
